// Jul-2026 (User-Wunsch) — Inzahlungnahme-Flow-Popups:
//  1) Nudge vor der Entscheidung (wurde der Kunde gefragt? + Provisions-Hinweis)
//  2) Wunschpreis-Erfassung bei „Ja"
//  3) Bestätigung bei „Nein" (Verzicht auf Provision)
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Repeat, AlertTriangle, HandCoins, ArrowLeft } from "lucide-react";

const fmtEur = (n) =>
    (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function TradeInNudgeModal({ commissionEur = 0, onBack, onClose, onContinue }) {
    // Feste Inzahlungnahme-Provision (€) wird vom Eltern-Panel (DispositionActions)
    // bereits geladen und als Prop durchgereicht — kein eigener Fetch (kein 401).
    const commission = Number(commissionEur) || 0;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-md" data-testid="dispo-modal-tradein-nudge">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-[#111827]" /> Inzahlungnahme aktiv anbieten</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm text-gray-700">
                    <p className="font-semibold">Wurde der Kunde gefragt, ob er ein gebrauchtes Fahrzeug hat?</p>
                    <p className="text-gray-600">Falls der Kunde es nicht in Zahlung geben möchte: Frag kurz nach dem Grund — oft lässt sich ein Eintausch doch noch erfassen.</p>
                    {commission > 0 && (
                        <div className="flex items-start gap-2 rounded-md bg-green-50 border border-green-200 p-3" data-testid="tradein-nudge-commission">
                            <HandCoins className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                            <span className="text-green-800">Für eine erfasste Inzahlungnahme erhältst du aktuell <b>{fmtEur(commission)}</b> Provision.</span>
                        </div>
                    )}
                </div>
                <div className="flex gap-2 pt-3">
                    {onBack && <Button type="button" variant="outline" onClick={onBack} className="rounded-sm" data-testid="tradein-nudge-back"><ArrowLeft className="w-4 h-4" /></Button>}
                    <Button type="button" onClick={onContinue} className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="tradein-nudge-continue">Weiter</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function TradeInWishPriceModal({ initialPrice = null, onBack, onClose, onSubmit }) {
    const [price, setPrice] = useState(initialPrice != null && initialPrice !== "" ? String(initialPrice) : "");
    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-md" data-testid="dispo-modal-tradein-wish">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-[#111827]" /> Wunschpreis des Kunden</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm text-gray-700">
                    <p>Welchen Preis stellt sich der Kunde für sein Eintausch-Fahrzeug vor? (Wird neben dem Ankaufpreis angezeigt.)</p>
                    <div className="flex items-center gap-1 max-w-[220px]">
                        <Input type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)}
                            autoFocus placeholder="z. B. 2500" className="h-10 rounded-sm font-mono-num" data-testid="tradein-wish-input" />
                        <span className="text-sm text-gray-400">€</span>
                    </div>
                </div>
                <div className="flex gap-2 pt-3">
                    {onBack && <Button type="button" variant="outline" onClick={onBack} className="rounded-sm" data-testid="tradein-wish-back"><ArrowLeft className="w-4 h-4" /></Button>}
                    <Button type="button" onClick={() => onSubmit?.(Number(price) || 0)} className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="tradein-wish-continue">Weiter zur Fahrzeugerfassung</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function TradeInDeclineConfirmModal({ commissionEur = 0, onBack, onClose, onConfirm }) {
    const commission = Number(commissionEur) || 0;
    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-md" data-testid="dispo-modal-tradein-confirm-no">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Wirklich keine Inzahlungnahme?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm text-gray-700">
                    <p className="font-semibold">Bist du sicher, dass der Kunde kein gebrauchtes Fahrzeug in Zahlung geben will?</p>
                    {commission > 0 && (
                        <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3">
                            <HandCoins className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                            <span className="text-red-800">Du verzichtest damit auf <b data-testid="tradein-decline-commission">{fmtEur(commission)}</b> Provision.</span>
                        </div>
                    )}
                </div>
                <div className="flex gap-2 pt-3">
                    <Button type="button" variant="outline" onClick={onBack} className="flex-1 rounded-sm font-bold" data-testid="tradein-decline-back">Zurück</Button>
                    <Button type="button" onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black rounded-sm uppercase tracking-wider" data-testid="tradein-decline-confirm">Trotzdem keine Inzahlungnahme</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
